/**
 * RequirementsHistoryDrawer Component
 * 
 * Drawer UI for viewing and restoring requirements refinement history
 * Shows history entries with timestamps, prompts, and restore functionality
 */

import React from 'react';
import { Drawer, List, Typography, Button, Empty, Tag, Space } from 'antd';
import { HistoryOutlined, RollbackOutlined } from '@ant-design/icons';
import { RequirementsHistoryEntry } from '../../../schemas/jobPostValidation';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import './RequirementsHistoryDrawer.scss';

dayjs.extend(relativeTime);

const { Text } = Typography;

interface RequirementsHistoryDrawerProps {
  visible: boolean;
  onClose: () => void;
  history: RequirementsHistoryEntry[];
  onRestore: (entry: RequirementsHistoryEntry) => void;
  currentRequirementsCount: number;
  initialPrompt?: string;
  initialRequirements?: Array<{
    id: number;
    topic: string;
    description: string;
    mustMatch: boolean;
  }>;
}

const RequirementsHistoryDrawer: React.FC<RequirementsHistoryDrawerProps> = ({
  visible,
  onClose,
  history,
  onRestore,
  currentRequirementsCount,
  initialPrompt,
  initialRequirements,
}) => {
  // Create initial entry if we have initial prompt and requirements
  const initialEntry: RequirementsHistoryEntry | null = 
    initialPrompt && initialRequirements && initialRequirements.length > 0
      ? {
          id: 'initial',
          timestamp: 0, // Oldest
          prompt: initialPrompt,
          requirementsBefore: [],
          requirementsAfter: initialRequirements,
          action: 'generate',
          summary: `Generated ${initialRequirements.length} initial requirements based on the job description.`,
        }
      : null;

  // Combine initial entry with history and sort by timestamp (newest first)
  const allHistory = initialEntry 
    ? [initialEntry, ...history].sort((a, b) => b.timestamp - a.timestamp)
    : [...history].sort((a, b) => b.timestamp - a.timestamp);

  const getRelativeTime = (timestamp: number): string => {
    return dayjs(timestamp).fromNow();
  };

  const handleRestore = (entry: RequirementsHistoryEntry) => {
    onRestore(entry);
    onClose();
  };

  return (
    <Drawer
      title={
        <Space>
          <HistoryOutlined />
          <span>Requirements Refinement History</span>
          {allHistory.length > 0 && (
            <Tag color="blue">{allHistory.length} {allHistory.length === 1 ? 'entry' : 'entries'}</Tag>
          )}
        </Space>
      }
      placement="right"
      width={500}
      onClose={onClose}
      open={visible}
      className="requirements-history-drawer"
    >
      {allHistory.length === 0 ? (
        <Empty
          description="No refinement history yet"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ marginTop: 60 }}
        >
          <Text type="secondary">
            Your refinement history will appear here after you refine requirements using AI.
          </Text>
        </Empty>
      ) : (
        <List
          dataSource={allHistory}
          renderItem={(entry, index) => {
            const isInitial = entry.id === 'initial';
            const isCurrentState = 
              entry.requirementsAfter.length === currentRequirementsCount &&
              entry.requirementsAfter.every((req, idx) => {
                const currentReq = entry.requirementsAfter[idx];
                return currentReq.description === req.description &&
                       currentReq.mustMatch === req.mustMatch;
              });

            return (
              <List.Item
                key={entry.id}
                className={`history-entry ${isCurrentState ? 'current-state' : ''} ${isInitial ? 'initial-entry' : ''}`}
                style={{ padding: '12px', cursor: 'pointer' }}
              >
                <div className="history-entry-content">
                  <div className="history-entry-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <Space size="small" wrap>
                      {isInitial ? (
                        <Tag color="orange">Original</Tag>
                      ) : (
                        <Tag color={entry.action === 'refine' ? 'blue' : 'purple'} style={{ margin: 0 }}>
                          {entry.action === 'refine' ? 'Refined' : 'Generated'}
                        </Tag>
                      )}
                      {!isInitial && (
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          {getRelativeTime(entry.timestamp)}
                        </Text>
                      )}
                      {isCurrentState && (
                        <Tag color="green" style={{ margin: 0 }}>Current</Tag>
                      )}
                    </Space>
                    {!isCurrentState && (
                      <Button
                        type="link"
                        icon={<RollbackOutlined />}
                        onClick={() => handleRestore(entry)}
                        size="small"
                        style={{ padding: 0, height: 'auto' }}
                      >
                        Restore
                      </Button>
                    )}
                  </div>

                  {/* Prompt Section */}
                  <div className="history-entry-section" style={{ marginBottom: 12 }}>
                    <Text strong style={{ fontSize: 12, color: '#262626', display: 'block', marginBottom: 4 }}>
                      Prompt
                    </Text>
                    <Text
                      ellipsis={{ tooltip: entry.prompt }}
                      style={{ fontSize: 12, lineHeight: 1.5, color: '#595959' }}
                    >
                      {entry.prompt}
                    </Text>
                  </div>

                  {/* Summary Section */}
                  {entry.summary && (
                    <div className="history-entry-section" style={{ marginBottom: 12 }}>
                      <Text strong style={{ fontSize: 12, color: '#262626', display: 'block', marginBottom: 4 }}>
                        Summary
                      </Text>
                      <Text
                        ellipsis={{ tooltip: entry.summary }}
                        style={{ fontSize: 12, lineHeight: 1.5, color: '#595959' }}
                      >
                        {entry.summary}
                      </Text>
                    </div>
                  )}

                  {/* Stats */}
                  <div className="history-entry-stats" style={{ display: 'flex', gap: 12, fontSize: 11, paddingTop: 8, borderTop: '1px solid #f0f0f0' }}>
                    {!isInitial && (
                      <Text type="secondary">
                        {entry.requirementsBefore.length} → 
                      </Text>
                    )}
                    <Text type="secondary">
                      {entry.requirementsAfter.length} requirement{entry.requirementsAfter.length !== 1 ? 's' : ''}
                    </Text>
                  </div>
                </div>
              </List.Item>
            );
          }}
        />
      )}
    </Drawer>
  );
};

export default RequirementsHistoryDrawer;

